/**
 * Communication Service - Central service for messages, templates, and reminders
 * Integrates with Firebase for real-time data synchronization
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import toast from 'react-hot-toast';
import { ConnectSource } from '../shared/connectSources';

// ==================== TYPES ====================

export interface Message {
  id: string;
  type: 'sms' | 'email';
  to: string;
  toPhone?: string;
  toEmail?: string;
  subject?: string;
  content: string;
  preview: string;
  // 'queued' = written to Firestore, awaiting the processOutboundSms CF to send via Twilio; 'suppressed' =
  // the CF ran where SMS isn't configured (staging) so nothing was sent. (Jose 2026-10-30)
  status: 'queued' | 'sent' | 'delivered' | 'failed' | 'pending' | 'received' | 'suppressed';
  sentAt: Date;
  deliveredAt?: Date;
  clientId?: string;
  appointmentId?: string;
  // 2-WAY (SLIK Connect, Jose 2026-09-25): 'out' = we sent, 'in' = client replied (inbound webhook).
  direction?: 'in' | 'out';
  read?: boolean;            // inbound read state (for the unread badge)
  phoneDigits?: string;      // last-10 digits — the THREAD KEY (robust to format drift)
  fromPhone?: string;        // inbound sender
  clientName?: string;
  // SOURCE (Jose 2026-10-30): WHERE this message came from (waitlist / receipt / reminder / staff manual …).
  // Drives the inbox chip + category filter. Inbound replies are treated as 'inbound' regardless. See
  // src/shared/connectSources.ts.
  source?: ConnectSource;
  sentByName?: string;       // staff member who typed a manual message (→ "Message from Kelly")
}

// A conversation in the Connect list — one per phone number, newest last-message first.
export interface ConnectThread {
  phoneDigits: string;       // thread key
  clientId: string;          // resolved when we can match the client
  clientName: string;        // display name (client name, else the phone)
  phone: string;             // a sendable number for this thread
  lastMessage: string;
  lastAt: Date;
  unread: number;            // unread inbound count
  lastSource?: ConnectSource;      // source of the newest message → the list-row chip
  lastDirection?: 'in' | 'out';    // direction of the newest message → "needs reply" grouping
}

export interface MessageTemplate {
  id: string;
  name: string;
  type: 'sms' | 'email';
  subject?: string;
  content: string;
  variables: string[];
  uses: number;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface AutoReminder {
  id: string;
  name: string;
  description: string;
  trigger: 'before_appointment' | 'after_appointment' | 'no_show' | 'review_request';
  triggerTime: number; // minutes/hours/days depending on trigger
  triggerUnit: 'minutes' | 'hours' | 'days';
  enabled: boolean;
  templateId?: string;
  messageContent?: string;
  sends: number;
  createdAt: Date;
}

export interface CommunicationStats {
  messagesToday: number;
  deliveryRate: number;
  pending: number;
  failed: number;
}

// ==================== SERVICE ====================

class CommunicationService {
  private static instance: CommunicationService;
  private messagesCollection = collection(db, 'communication_messages');
  private templatesCollection = collection(db, 'communication_templates');
  private remindersCollection = collection(db, 'communication_reminders');
  private cachedMessages: Message[] = [];
  private cachedTemplates: MessageTemplate[] = [];
  private cachedReminders: AutoReminder[] = [];
  private _syncUnsubs: Array<() => void> = [];

  private constructor() {
    this.initializeRealTimeSync();
    // RESILIENCE (Jose 2026-10-29 audit): subscribes at module load (pre-auth) → re-subscribe once
    // auth is ready so denied cold reads self-heal (unsub prior first). See shiftService fix.
    try { onAuthStateChanged(auth, (u) => { if (u) this.initializeRealTimeSync(); }); } catch { /* ignore */ }
  }

  static getInstance(): CommunicationService {
    if (!CommunicationService.instance) {
      CommunicationService.instance = new CommunicationService();
    }
    return CommunicationService.instance;
  }

  // ==================== INITIALIZATION ====================

  private initializeRealTimeSync(): void {
    // Tear down any prior listeners first so an auth-ready re-subscribe never stacks duplicates.
    try { this._syncUnsubs.forEach(u => { try { u(); } catch { /* ignore */ } }); } catch { /* ignore */ }
    this._syncUnsubs = [];

    // Messages sync (recent 100)
    const messagesQuery = query(this.messagesCollection, orderBy('sentAt', 'desc'), limit(100));
    this._syncUnsubs.push(onSnapshot(messagesQuery, (snapshot) => {
      this.cachedMessages = snapshot.docs.map(doc => this.convertToMessage(doc.id, doc.data()));
    }, (error) => {
      console.error('Messages sync error:', error);
    }));

    // Templates sync
    const templatesQuery = query(this.templatesCollection, orderBy('name', 'asc'));
    this._syncUnsubs.push(onSnapshot(templatesQuery, (snapshot) => {
      this.cachedTemplates = snapshot.docs.map(doc => this.convertToTemplate(doc.id, doc.data()));
    }, (error) => {
      console.error('Templates sync error:', error);
    }));

    // Reminders sync
    const remindersQuery = query(this.remindersCollection, orderBy('name', 'asc'));
    this._syncUnsubs.push(onSnapshot(remindersQuery, (snapshot) => {
      this.cachedReminders = snapshot.docs.map(doc => this.convertToReminder(doc.id, doc.data()));
    }, (error) => {
      console.error('Reminders sync error:', error);
    }));
  }

  // ==================== CONVERTERS ====================

  private convertToMessage(id: string, data: any): Message {
    return {
      id,
      type: data.type || 'sms',
      to: data.to || '',
      toPhone: data.toPhone,
      toEmail: data.toEmail,
      subject: data.subject,
      content: data.content || '',
      preview: data.preview || data.content?.substring(0, 100) || '',
      status: data.status || 'pending',
      sentAt: data.sentAt?.toDate?.() || new Date(),
      deliveredAt: data.deliveredAt?.toDate?.(),
      clientId: data.clientId,
      appointmentId: data.appointmentId,
      direction: data.direction || 'out',
      read: data.read ?? true,
      phoneDigits: data.phoneDigits,
      fromPhone: data.fromPhone,
      clientName: data.clientName,
      source: data.source,
      sentByName: data.sentByName,
    };
  }

  // ==================== 2-WAY (SLIK Connect) ====================
  // Thread key = last-10 digits of the phone, so inbound (webhook) and outbound thread together
  // regardless of stored format. (Jose 2026-09-25)

  private last10(p?: string): string {
    return String(p || '').replace(/\D/g, '').slice(-10);
  }

  /** Send an SMS to a client AND log it into the thread (direction 'out'), tagged with its source. */
  async sendToClient(params: { clientId?: string; clientName: string; phone: string; appointmentId?: string; body: string; includeSaveContact?: boolean; source?: ConnectSource; sentByName?: string; }): Promise<boolean> {
    const digits = this.last10(params.phone);
    // FIRST-CONTACT vCard NUDGE (Jose 2026-10-23): the FIRST time we ever text a number, append the
    // "Save SLIK Connect" link so our texts show a NAME, not an unknown/spam number. Once per client —
    // skipped on every message after the first (no spam). Pass includeSaveContact:false to force-suppress.
    let body = params.body;
    try {
      if (params.includeSaveContact !== false && digits) {
        const prior = await getDocs(query(this.messagesCollection, where('phoneDigits', '==', digits), limit(1)));
        if (prior.empty) {
          body = `${body}\n\nSave us so you always know it's us (not spam): https://book.slikbarberlounge.com/connect`;
        }
      }
    } catch (e) {
      console.error('sendToClient first-contact check failed (sending without link):', e);
    }
    // DURABLE OUTBOUND QUEUE (Jose 2026-10-30): write the message as `status:'queued'` and let the
    // processOutboundSms Cloud Function send it via Twilio + flip the status. NO client-side fetch — a
    // Firestore write is offline-durable + SDK-retried, so a text survives the shop's flaky WiFi (the old
    // direct fetch to /sendSms died with "TypeError: Load failed" on a blip). The thread shows the message
    // instantly as "sending…" and live-updates to sent/failed when the CF reports the real result.
    try {
      await addDoc(this.messagesCollection, {
        type: 'sms',
        direction: 'out',
        read: true,
        to: params.clientName || params.phone,
        toPhone: params.phone,
        phoneDigits: digits,
        clientId: params.clientId || '',
        clientName: params.clientName || params.phone,
        appointmentId: params.appointmentId || '',
        content: body,
        preview: body.slice(0, 140),
        status: 'queued',
        sentAt: serverTimestamp(),
        source: params.source || 'manual',
        ...(params.sentByName ? { sentByName: params.sentByName } : {}),
      });
      return true;
    } catch (e) {
      console.error('sendToClient queue failed:', e);
      toast.error('Message could not be queued — check the connection and try again.');
      return false;
    }
  }

  /** Live subscription to ONE conversation (by phone-digits thread key), oldest→newest. */
  subscribeToThread(phoneDigits: string, cb: (msgs: Message[]) => void): () => void {
    const q = query(this.messagesCollection, where('phoneDigits', '==', phoneDigits), orderBy('sentAt', 'asc'));
    return onSnapshot(q, (snap) => cb(snap.docs.map((d) => this.convertToMessage(d.id, d.data()))),
      (e) => console.error('thread sync error:', e));
  }

  /** Live subscription to the conversation LIST (one per phone), newest first. */
  subscribeToThreads(cb: (threads: ConnectThread[]) => void): () => void {
    const q = query(this.messagesCollection, orderBy('sentAt', 'desc'), limit(400));
    return onSnapshot(q, (snap) => {
      const byPhone = new Map<string, ConnectThread>();
      snap.docs.forEach((d) => {
        const m = this.convertToMessage(d.id, d.data());
        const key = m.phoneDigits || this.last10(m.toPhone || m.fromPhone);
        if (!key) return;
        const isUnread = m.direction === 'in' && !m.read;
        const existing = byPhone.get(key);
        if (!existing) {
          byPhone.set(key, {
            phoneDigits: key,
            clientId: m.clientId || '',
            clientName: m.clientName || m.toPhone || m.fromPhone || key,
            phone: m.fromPhone || m.toPhone || key,
            lastMessage: m.preview || m.content,
            lastAt: m.sentAt,
            unread: isUnread ? 1 : 0,
            lastSource: m.source,      // newest message (list is ordered desc, so first seen = newest)
            lastDirection: m.direction,
          });
        } else {
          if (isUnread) existing.unread += 1;
          if (!existing.clientId && m.clientId) existing.clientId = m.clientId;
          if ((!existing.clientName || existing.clientName === key) && m.clientName) existing.clientName = m.clientName;
        }
      });
      cb(Array.from(byPhone.values()).sort((a, b) => b.lastAt.getTime() - a.lastAt.getTime()));
    }, (e) => console.error('threads sync error:', e));
  }

  /** Mark every inbound message in a thread as read (clears the unread badge). */
  async markThreadRead(phoneDigits: string): Promise<void> {
    try {
      const q = query(this.messagesCollection, where('phoneDigits', '==', phoneDigits), where('direction', '==', 'in'), where('read', '==', false));
      const snap = await getDocs(q);
      await Promise.all(snap.docs.map((d) => updateDoc(d.ref, { read: true })));
    } catch (e) {
      console.error('markThreadRead failed:', e);
    }
  }

  private convertToTemplate(id: string, data: any): MessageTemplate {
    return {
      id,
      name: data.name || '',
      type: data.type || 'sms',
      subject: data.subject,
      content: data.content || '',
      variables: data.variables || [],
      uses: data.uses || 0,
      active: data.active ?? true,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date()
    };
  }

  private convertToReminder(id: string, data: any): AutoReminder {
    return {
      id,
      name: data.name || '',
      description: data.description || '',
      trigger: data.trigger || 'before_appointment',
      triggerTime: data.triggerTime || 24,
      triggerUnit: data.triggerUnit || 'hours',
      enabled: data.enabled ?? false,
      templateId: data.templateId,
      messageContent: data.messageContent,
      sends: data.sends || 0,
      createdAt: data.createdAt?.toDate?.() || new Date()
    };
  }

  // ==================== MESSAGES ====================

  async getMessages(limitCount: number = 50): Promise<Message[]> {
    try {
      const snapshot = await getDocs(
        query(this.messagesCollection, orderBy('sentAt', 'desc'), limit(limitCount))
      );
      return snapshot.docs.map(doc => this.convertToMessage(doc.id, doc.data()));
    } catch (error) {
      console.error('Error fetching messages:', error);
      return this.cachedMessages;
    }
  }

  async getMessagesByClient(clientId: string): Promise<Message[]> {
    try {
      const snapshot = await getDocs(
        query(this.messagesCollection, where('clientId', '==', clientId), orderBy('sentAt', 'desc'))
      );
      return snapshot.docs.map(doc => this.convertToMessage(doc.id, doc.data()));
    } catch (error) {
      console.error('Error fetching client messages:', error);
      return [];
    }
  }

  async sendMessage(message: Omit<Message, 'id' | 'sentAt' | 'status'>): Promise<string> {
    try {
      const docRef = await addDoc(this.messagesCollection, {
        ...message,
        preview: message.content.substring(0, 100),
        status: 'pending',
        sentAt: serverTimestamp()
      });

      // In a real app, this would trigger an actual SMS/email service
      // For now, simulate delivery
      setTimeout(async () => {
        await updateDoc(doc(this.messagesCollection, docRef.id), {
          status: 'delivered',
          deliveredAt: serverTimestamp()
        });
      }, 2000);

      toast.success(`${message.type.toUpperCase()} sent to ${message.to}`);
      return docRef.id;
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      throw error;
    }
  }

  async resendMessage(id: string): Promise<void> {
    try {
      const docRef = doc(this.messagesCollection, id);
      await updateDoc(docRef, {
        status: 'pending',
        sentAt: serverTimestamp()
      });

      setTimeout(async () => {
        await updateDoc(docRef, {
          status: 'delivered',
          deliveredAt: serverTimestamp()
        });
      }, 2000);

      toast.success('Message resent');
    } catch (error) {
      console.error('Error resending message:', error);
      toast.error('Failed to resend message');
      throw error;
    }
  }

  // ==================== TEMPLATES ====================

  async getTemplates(): Promise<MessageTemplate[]> {
    try {
      const snapshot = await getDocs(query(this.templatesCollection, orderBy('name', 'asc')));
      return snapshot.docs.map(doc => this.convertToTemplate(doc.id, doc.data()));
    } catch (error) {
      console.error('Error fetching templates:', error);
      return this.cachedTemplates;
    }
  }

  async getTemplate(id: string): Promise<MessageTemplate | null> {
    try {
      const docRef = doc(this.templatesCollection, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return this.convertToTemplate(docSnap.id, docSnap.data());
      }
      return null;
    } catch (error) {
      console.error('Error fetching template:', error);
      return null;
    }
  }

  async createTemplate(template: Omit<MessageTemplate, 'id' | 'createdAt' | 'updatedAt' | 'uses'>): Promise<string> {
    try {
      const docRef = await addDoc(this.templatesCollection, {
        ...template,
        uses: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      toast.success('Template created successfully');
      return docRef.id;
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
      throw error;
    }
  }

  async updateTemplate(id: string, updates: Partial<MessageTemplate>): Promise<void> {
    try {
      const docRef = doc(this.templatesCollection, id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      toast.success('Template updated');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
      throw error;
    }
  }

  async deleteTemplate(id: string): Promise<void> {
    try {
      await deleteDoc(doc(this.templatesCollection, id));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
      throw error;
    }
  }

  async incrementTemplateUse(id: string): Promise<void> {
    try {
      const docRef = doc(this.templatesCollection, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const currentUses = docSnap.data().uses || 0;
        await updateDoc(docRef, { uses: currentUses + 1 });
      }
    } catch (error) {
      console.error('Error incrementing template use:', error);
    }
  }

  // ==================== REMINDERS ====================

  async getReminders(): Promise<AutoReminder[]> {
    try {
      const snapshot = await getDocs(query(this.remindersCollection, orderBy('name', 'asc')));
      return snapshot.docs.map(doc => this.convertToReminder(doc.id, doc.data()));
    } catch (error) {
      console.error('Error fetching reminders:', error);
      return this.cachedReminders;
    }
  }

  async createReminder(reminder: Omit<AutoReminder, 'id' | 'createdAt' | 'sends'>): Promise<string> {
    try {
      const docRef = await addDoc(this.remindersCollection, {
        ...reminder,
        sends: 0,
        createdAt: serverTimestamp()
      });
      toast.success('Auto reminder created');
      return docRef.id;
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Failed to create reminder');
      throw error;
    }
  }

  async updateReminder(id: string, updates: Partial<AutoReminder>): Promise<void> {
    try {
      const docRef = doc(this.remindersCollection, id);
      await updateDoc(docRef, updates);
      toast.success('Reminder updated');
    } catch (error) {
      console.error('Error updating reminder:', error);
      toast.error('Failed to update reminder');
      throw error;
    }
  }

  async toggleReminder(id: string, enabled: boolean): Promise<void> {
    try {
      const docRef = doc(this.remindersCollection, id);
      await updateDoc(docRef, { enabled });
      toast.success(`Reminder ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling reminder:', error);
      toast.error('Failed to update reminder');
      throw error;
    }
  }

  async deleteReminder(id: string): Promise<void> {
    try {
      await deleteDoc(doc(this.remindersCollection, id));
      toast.success('Reminder deleted');
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error('Failed to delete reminder');
      throw error;
    }
  }

  // ==================== STATS ====================

  async getStats(): Promise<CommunicationStats> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    try {
      const todayQuery = query(
        this.messagesCollection,
        where('sentAt', '>=', Timestamp.fromDate(today))
      );
      const snapshot = await getDocs(todayQuery);
      const todayMessages = snapshot.docs.map(doc => this.convertToMessage(doc.id, doc.data()));

      const delivered = todayMessages.filter(m => m.status === 'delivered').length;
      const pending = todayMessages.filter(m => m.status === 'pending').length;
      const failed = todayMessages.filter(m => m.status === 'failed').length;
      const total = todayMessages.length;

      return {
        messagesToday: total,
        deliveryRate: total > 0 ? Math.round((delivered / total) * 100) : 100,
        pending,
        failed
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return {
        messagesToday: 0,
        deliveryRate: 100,
        pending: 0,
        failed: 0
      };
    }
  }

  // ==================== REAL-TIME SUBSCRIPTIONS ====================

  // H29 (Jose 2026-10-29 audit): all public subscribe methods carry an error callback so a mid-session
  // permission/quota/network failure is logged (never a silent freeze on stale data).
  subscribeToMessages(callback: (messages: Message[]) => void, limitCount: number = 50): () => void {
    const q = query(this.messagesCollection, orderBy('sentAt', 'desc'), limit(limitCount));
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => this.convertToMessage(doc.id, doc.data()));
      callback(messages);
    }, (error) => console.error('subscribeToMessages error:', error));
  }

  subscribeToTemplates(callback: (templates: MessageTemplate[]) => void): () => void {
    const q = query(this.templatesCollection, orderBy('name', 'asc'));
    return onSnapshot(q, (snapshot) => {
      const templates = snapshot.docs.map(doc => this.convertToTemplate(doc.id, doc.data()));
      callback(templates);
    }, (error) => console.error('subscribeToTemplates error:', error));
  }

  subscribeToReminders(callback: (reminders: AutoReminder[]) => void): () => void {
    const q = query(this.remindersCollection, orderBy('name', 'asc'));
    return onSnapshot(q, (snapshot) => {
      const reminders = snapshot.docs.map(doc => this.convertToReminder(doc.id, doc.data()));
      callback(reminders);
    }, (error) => console.error('subscribeToReminders error:', error));
  }

  // ==================== CLOSURE EVENTS ====================

  private closureEventsCollection = collection(db, 'closure_events');

  async logClosureEvent(event: {
    dates: string[];
    reason: string;
    reasonText: string;
    message: string;
    stats: ClosureEventStats;
  }): Promise<string> {
    try {
      const docRef = await addDoc(this.closureEventsCollection, {
        ...event,
        createdAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error logging closure event:', error);
      throw error;
    }
  }

  async getClosureEventHistory(limitCount: number = 10): Promise<ClosureEvent[]> {
    try {
      const snapshot = await getDocs(
        query(this.closureEventsCollection, orderBy('createdAt', 'desc'), limit(limitCount))
      );
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      } as ClosureEvent));
    } catch (error) {
      console.error('Error fetching closure events:', error);
      return [];
    }
  }

  subscribeToClosureEvents(callback: (events: ClosureEvent[]) => void, limitCount: number = 10): () => void {
    const q = query(this.closureEventsCollection, orderBy('createdAt', 'desc'), limit(limitCount));
    return onSnapshot(q, (snapshot) => {
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      } as ClosureEvent));
      callback(events);
    }, (error) => console.error('subscribeToClosureEvents error:', error));
  }
}

// ==================== CLOSURE EVENT TYPES ====================

export interface ClosureEventStats {
  totalClients: number;
  totalAppointments: number;
  smsSent: number;
  smsDelivered: number;
  smsFailed: number;
  emailsSent: number;
  emailsOpened: number;
  emailsClicked: number;
  appointmentsCancelled: number;
}

export interface ClosureEvent {
  id: string;
  dates: string[];
  reason: string;
  reasonText: string;
  message: string;
  stats: ClosureEventStats;
  createdAt: Date;
}

// Export singleton instance
export const communicationService = CommunicationService.getInstance();
export default communicationService;
